import React, { useRef, useState } from 'react';
import { Anohana, Btn, Bx, CheckboxControl, IconBtn, Typo } from '@curry-group/mui-curcuma';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { ISurvey } from '../../../model/survey';

/**
 * Mandatory survey shown after login. Multiple choice, at least one answer needed
 * to submit. The close icon and an outside click only dismiss it for this browser
 * session — it comes back at the next login. "Nicht wieder anzeigen" opts out for
 * good.
 */
export const SurveyDialog: React.FC<{
  open: boolean;
  survey?: ISurvey | null;
  onSubmit: (answerIds: Array<string>) => void;
  onDecline: () => void;
  onDismiss: () => void;
}> = ({ open, survey, onSubmit, onDecline, onDismiss }) => {
  const [selected, setSelected] = useState<Array<string>>([]);
  // Answering clears the survey from the store immediately; keep the last one around
  // so the dialog can still play its closing transition instead of vanishing.
  const lastSurvey = useRef(survey);
  if (survey) {
    lastSurvey.current = survey;
  }
  const shown = survey || lastSurvey.current;

  if (!shown) {
    return <></>;
  }

  const toggle = (answerId: string, checked: boolean) => {
    setSelected(current => (checked ? [...current, answerId] : current.filter(id => id !== answerId)));
  };

  return (
    <Anohana open={!!open} transition="zoom" disableScrollLock fullWidth maxWidth="sm" onClose={onDismiss}>
      <Bx p={2.5}>
        <Bx display="flex" alignItems="flex-start" mb={3}>
          <Typo variant="h4" component="span">
            {shown.title || 'Kurze Umfrage'}
          </Typo>
          <Bx ml="auto" mr={-1}>
            <IconBtn onClick={onDismiss} title="Schließen">
              <FontAwesomeIcon icon={faTimes} />
            </IconBtn>
          </Bx>
        </Bx>

        <Bx mb={2}>
          <Typo variant="subtitle1" component="p">
            {shown.question}
          </Typo>
        </Bx>

        <Bx mb={3}>
          {shown.answers?.map(answer => (
            <Bx key={answer.answerId} mb={1}>
              <CheckboxControl
                control={{
                  color: 'primary',
                  name: answer.answerId,
                  value: selected.includes(answer.answerId),
                  onChange: e => toggle(answer.answerId, e.target.checked),
                }}
                checked={selected.includes(answer.answerId)}
                label={<Typo variant="body1">{answer.answerText}</Typo>}
              />
            </Bx>
          ))}
        </Bx>

        <Bx display="flex" flexWrap="wrap" mt={3} justifyContent="flex-end">
          <Bx mr={3}>
            <Btn variant="outlined" size="large" startIcon={<FontAwesomeIcon icon={faEyeSlash} />} onClick={onDecline}>
              Nicht wieder anzeigen
            </Btn>
          </Bx>
          <Bx>
            <Btn
              variant="contained"
              color="success"
              size="large"
              disabled={!selected.length}
              startIcon={<FontAwesomeIcon icon={faCheck} />}
              onClick={() => onSubmit(selected)}
            >
              Absenden
            </Btn>
          </Bx>
        </Bx>
      </Bx>
    </Anohana>
  );
};
